import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChatChannelType, type IChatConversation } from 'src/@types/chat';
import { routes } from 'src/Router/paths';
import { createChannel } from 'src/redux/slices/chat';
import { dispatch } from 'src/redux/store';
import { useToggleConversation } from './useToggleConversation';

export const useDirectMessagesStatuses = (
  directMessages: IChatConversation[],
) => {
  const [chatLoading, setChatLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toggleConversation } = useToggleConversation();

  const chatChannelsByPartnerUserMap = useMemo(
    () =>
      directMessages.reduce(
        (acc, chat) => {
          const otherUser = chat.chatPartner?.user;

          if (!otherUser) return acc;

          return {
            ...acc,
            [otherUser]: chat,
          };
        },
        {} as Record<string, IChatConversation>,
      ),
    [directMessages],
  );

  const checkStatusWithUser = (chatPartnerUserId: string) => {
    const chatChannel = chatChannelsByPartnerUserMap[chatPartnerUserId];

    if (!chatChannel) return 'new';

    if (chatChannel?.hidden === true) return 'reopen';

    return 'open';
  };

  const goToChat = (channel: IChatConversation) => {
    navigate(routes.dashboard.chat.view(channel._id));
  };

  const resolveChatChannel = async (
    chatPartnerUserId: string,
  ): Promise<IChatConversation> => {
    const status = checkStatusWithUser(chatPartnerUserId);
    const chatChannel = chatChannelsByPartnerUserMap[chatPartnerUserId];

    if (status === 'open') return chatChannel;

    if (status === 'reopen') {
      return toggleConversation({ channelId: chatChannel._id, hide: false });
    }

    return new Promise((resolve) => {
      dispatch(
        createChannel({
          type: ChatChannelType.TEXT,
          p2p: true,
          name: chatPartnerUserId,
          participants: [{ _id: chatPartnerUserId }],
          onSuccess: (newChat) => {
            resolve(newChat);
          },
        }),
      );
    });
  };

  const handleGoToChat = async (
    chatPartnerUserId: string,
  ): Promise<IChatConversation | null> => {
    if (chatLoading) return null;
    setChatLoading(chatPartnerUserId);

    const chatChannel = await resolveChatChannel(chatPartnerUserId);
    goToChat(chatChannel);

    setChatLoading(null);
    return chatChannel;
  };

  return {
    checkStatusWithUser,
    handleGoToChat,
    resolveChatChannel,
    chatLoading,
  };
};
